import {
  NetworkNodeFormValues,
  OnboardingUserFormValues,
  RegulationFormValues,
} from '../../../types';
import {
  BlockActionResult,
  formatUsageScreensMessage,
  walkNetworkNodes,
} from '../../networkReferenceUtils';

export type UserUsage = {
  regulations: RegulationFormValues[];
};

export function findUserUsage(
  network: NetworkNodeFormValues[],
  userId: string,
): UserUsage {
  const regulations: RegulationFormValues[] = [];

  walkNetworkNodes(network).forEach(({node}) => {
    (node.regulations ?? []).forEach((regulation: RegulationFormValues) => {
      if (regulation.assignee === userId) {
        regulations.push(regulation);
      }
    });
  });

  return {regulations};
}

export function isUserInUse(usage: UserUsage): boolean {
  return usage.regulations.length > 0;
}

const getUserUsageScreens = (usage: UserUsage): string[] =>
  usage.regulations.length > 0 ? ['רגולציה'] : [];

const getUserDisplayName = (user: OnboardingUserFormValues): string =>
  `${user.firstName} ${user.lastName}`.trim() || user.email || user.id;

export function formatUserDeleteBlockMessage(
  user: OnboardingUserFormValues,
  usage: UserUsage,
): string {
  const screens = formatUsageScreensMessage(getUserUsageScreens(usage));
  return `לא ניתן למחוק את המשתמש "${getUserDisplayName(user)}" — הוא בשימוש במסכי ${screens}. יש להסיר את השיוכים תחילה`;
}

export function canDeleteUser(
  network: NetworkNodeFormValues[],
  user: OnboardingUserFormValues,
): BlockActionResult {
  const usage = findUserUsage(network, user.id);
  if (isUserInUse(usage)) {
    return {
      allowed: false,
      message: formatUserDeleteBlockMessage(user, usage),
    };
  }

  return {allowed: true};
}

export function formatUsersDeleteBlockMessage(
  inUse: Array<{user: OnboardingUserFormValues; usage: UserUsage}>,
): string {
  const screens = new Set<string>();
  inUse.forEach(({usage}) => {
    getUserUsageScreens(usage).forEach(screen => screens.add(screen));
  });

  const userNames = inUse
    .map(({user}) => `"${getUserDisplayName(user)}"`)
    .join(', ');
  return `לא ניתן למחוק את המשתמשים ${userNames} — הם בשימוש במסכי ${formatUsageScreensMessage([...screens])}. יש להסיר את השיוכים תחילה`;
}

export function canDeleteUsers(
  network: NetworkNodeFormValues[],
  users: OnboardingUserFormValues[],
): BlockActionResult {
  const inUse = users
    .map(user => ({user, usage: findUserUsage(network, user.id)}))
    .filter(({usage}) => isUserInUse(usage));

  if (inUse.length === 0) {
    return {allowed: true};
  }

  if (inUse.length === 1) {
    return {
      allowed: false,
      message: formatUserDeleteBlockMessage(inUse[0].user, inUse[0].usage),
    };
  }

  return {allowed: false, message: formatUsersDeleteBlockMessage(inUse)};
}
